import useEscapeKey from "@hooks/useEscapeKey";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { VulnerabilityBadge } from "./Badge";
import useVulnerabilityPolicy from "@hooks/useVulnerabilityPolicy";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { OutlineButton } from "@components/Buttons";

const VulnerabilityDetailModal = ({ isOpen, onClose, data = null, "data-testid": dataTestId = "vulnerability-detail-modal" }) => {
    const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
    const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
    const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
    const { t } = useTranslation();
    const { colors } = useTheme();
    const { vulnerabilityLevel } = useVulnerabilityPolicy();
    useEscapeKey(onClose, isOpen);

    const componentSummary = useMemo(() => {
        if (!data) return [];
        return [
            {
                name: "importance-level",
                label: t("vulnerability-policy.importance-level-cd"),
                value: t(`vulnerability-policy.importance-level-${data?.importanceLevelCdName?.toLowerCase() || ''}`, data?.importanceLevelCdName) || "---",
            },
            {
                name: "weakness",
                label: t("vulnerability-policy.weakness-cd"),
                value: data?.weaknessCdName || "---",
            },
            {
                name: "vulnerability-security-code",
                label: t("vulnerability-policy.vulnerability-security-code"),
                value: data?.vulnerabilityLevel || "---",
            },
            {
                name: "followup-content",
                label: t("vulnerability-policy.followup-content"),
                value: data?.followupContent || "---",
            },
        ];
    }, [data, t]);

    if (!isOpen) return null;

    const badgeLevel = data?.vulnerabilityLevel;

    const modalContent = (
        <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
            <div className={`${colors.bgModal} w-[1000px] min-w-[1000px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`} data-testid={dataTestId}>
                {/* Header */}
                <div className={`flex flex-shrink-0 items-center justify-center gap-3 border-b px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
                    {badgeLevel && (
                        <VulnerabilityBadge
                            level={badgeLevel}
                            bgColor={vulnerabilityLevel.find(item => item.levelCode === badgeLevel)?.vulnerabilityLevel}
                            className="font-extrabold cursor-pointer hover:opacity-80 transition-opacity"
                        />
                    )}
                    <h2 className={`text-center text-xl font-bold uppercase tracking-wide ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
                        {t("vulnerability-policy.vulnerability-details")}
                    </h2>
                </div>

                {/* Content */}
                <div className="px-6 py-4 pb-4 flex-1 min-h-0 overflow-y-auto" data-testid={`${dataTestId}-content`}>
                    <div className={`rounded-lg border p-4 ${colors.bgInput} ${colors.borderPrimary}`}>
                        {componentSummary.length > 0 && (
                            <div className="grid grid-cols-2 gap-4 items-start">
                                {componentSummary.map((item) => (
                                    <div
                                        key={item.name}
                                        className={`rounded-lg border px-4 py-3 ${colors.bgInput} ${colors.borderPrimary}`}
                                        data-testid={fieldContainerTestId(item.name)}
                                        style={{
                                            height: "100px",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <p className={`text-xs uppercase tracking-wide ${colors.textMuted}`} data-testid={fieldLabelTestId(item.name)}>{item.label}</p>
                                        <p className={`mt-1 break-words whitespace-normal text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId(item.name)} style={{
                                            flex: 1,
                                            maxHeight: "3.6em",
                                            overflowY: "auto",
                                            lineHeight: "1.2em",
                                        }}>{item.value || "---"}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className={`flex flex-shrink-0 justify-center border-t px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
                    <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>{t("common.close-button")}</OutlineButton>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default VulnerabilityDetailModal;

